import gsap from 'gsap';
import { MotionPathPlugin } from 'gsap/MotionPathPlugin';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Swiper from 'swiper';
import { createApp, onMounted, onUnmounted, ref, watch } from 'vue';

import { useVideo } from '~/composables/use-video';
import { useViewportSize } from '~/composables/use-viewport-size';
import { ANIMATION_CONFIGS, ANIMATION_EASES } from '~/constants/animations';
import * as DataDogLogs from '~/lib/datadog/datadog';

import { BaseSection } from './base-section';

gsap.registerPlugin(MotionPathPlugin, ScrollTrigger);

/**
 * @type {import('swiper/types').SwiperOptions}
 */
const SWIPER_SETTINGS = {
  breakpoints: {
    0: {
      slidesPerView: 1,
    },
    340: {
      slidesPerView: 1.5,
    },
  },
  centeredSlides: true,
  centeredSlidesBounds: true,
  slidesOffsetBefore: 30,
};

/**
 * Material Hero Feature Section
 *
 * This section includes a texts, images and callouts with animated circles and background.
 * The section implements an inView animation using GSAP when the section comes into view.
 *
 * @extends BaseSection
 * @constructor
 * @param {HTMLElement} container
 */
class MaterialHeroFeature extends BaseSection {
  constructor() {
    super('[data-section-type="product-material-hero-feature"]');
  }

  init(instanceData) {
    const container = instanceData.container;

    if (!container) {
      DataDogLogs.error(
        '[Allbirds section] - Material Hero Feature - init - Container not found',
        {},
        new Error('Container not found'),
      );
      return;
    }

    createApp({
      name: 'MaterialHeroFeatureSection',
      setup() {
        const videoRefs = ref({});
        const carousel = ref(null);
        const floatingImageAnimation = ref(null);
        const { isMobile } = useViewportSize();

        const initSwiper = () => {
          const swiperContainer = container.querySelector('.swiper');
          if (swiperContainer) {
            carousel.value?.destroy();
            carousel.value = new Swiper(swiperContainer, SWIPER_SETTINGS);
          }
        };

        onMounted(() => {
          const videos = container.querySelectorAll('video');
          videos.forEach((video) => {
            const videoId = video.id;
            if (!videoId) return;
            const videoComposable = useVideo(videoId);
            videoComposable.init();
            videoRefs.value[videoId] = videoComposable;
          });

          if (isMobile.value) {
            initSwiper();
          }

          const floatingImage = container.querySelector('.floating_img');
          if (floatingImage) {
            floatingImageAnimation.value = gsap.fromTo(
              floatingImage,
              { yPercent: 200 },
              {
                scrollTrigger: {
                  end: '50% 50%',
                  scrub: 0.3,
                  start: '0% 50%',
                  trigger: container,
                },
                yPercent: 0,
              },
            );
          }
        });

        watch(isMobile, () => {
          if (isMobile.value && !carousel.value) {
            initSwiper();
          }
        });

        onUnmounted(() => {
          carousel.value?.destroy();
          carousel.value = null;
          floatingImageAnimation.value?.scrollTrigger?.kill();
          floatingImageAnimation.value = null;
        });

        return { videoRefs };
      },
    }).mount(container);
  }

  /**
   * Sets up the animation timeline for the section that will play when it comes into view
   * This will be called automatically during initialization
   *
   * @param {Object} instanceData - The section instance data
   * @return {Object} The GSAP timeline for the animation
   */
  setupInViewAnimationTimeline(instanceData) {
    const container = instanceData.container;
    const { isMobile } = useViewportSize();

    if (!container) {
      return null;
    }

    const tl = gsap.timeline({
      paused: true,
      scrollTrigger: {
        ...this.getInViewAnimationOptions(),
        trigger: container,
      },
    });

    if (!tl) {
      return null;
    }

    const circle1 = container.querySelector('.circle-1');
    if (circle1) {
      tl.to(
        circle1,
        {
          attr: { r: 70 },
          duration: 1.5,
          ease: ANIMATION_EASES.SINE_OUT,
          opacity: 1,
        },
        0,
      );
    }

    const circle2 = container.querySelector('.circle-2');
    if (circle2) {
      tl.to(
        circle2,
        {
          attr: { r: 80 },
          duration: 1.5,
          ease: ANIMATION_EASES.SINE_OUT,
          opacity: 1,
        },
        0,
      );
    }

    const circle3 = container.querySelector('.circle-3');
    if (circle3) {
      tl.to(
        circle3,
        {
          attr: { r: 90 },
          duration: 1.5,
          ease: ANIMATION_EASES.SINE_OUT,
          opacity: 1,
        },
        0,
      );
    }

    const callouts = container.querySelectorAll('.callout');
    if (callouts.length && !isMobile.value) {
      const dots = container.querySelectorAll('.dot');
      const targetPositions = [0.681, 0.825, 0.175, 0.3243];
      dots.forEach((dot, i) => {
        tl.to(
          dot,
          {
            attr: { r: 1.5 },
            delay: i * 0.4,
            duration: 1.5,
            ease: ANIMATION_EASES.SINE_OUT,
            motionPath: {
              align: '#motion-path-circle',
              autoRotate: false,
              end: targetPositions[i],
              path: '#motion-path-circle',
              start: targetPositions[i] + 0.15,
            },
            opacity: 1,
          },
          0.8,
        );
      });

      tl.to(
        [callouts[3], callouts[2], callouts[0], callouts[1]],
        {
          duration: 0.5,
          ease: ANIMATION_EASES.SINE_OUT,
          opacity: 1,
          stagger: 0.4,
        },
        1.6,
      );
    }

    const swiper = container.querySelector('.swiper');
    if (swiper && isMobile.value) {
      tl.to(swiper, ANIMATION_CONFIGS.FADE_IN, 1.4);
    }

    const floatingImage = container.querySelector('.floating_img');
    const centeredImage = container.querySelector('.center_img');
    if (floatingImage) {
      tl.to(
        floatingImage,
        {
          duration: 0.6,
          ease: ANIMATION_EASES.SINE_OUT,
          opacity: 1,
        },
        1.4,
      );
    } else if (centeredImage) {
      tl.to(
        centeredImage,
        {
          duration: 1,
          ease: ANIMATION_EASES.SINE_OUT,
          opacity: 1,
        },
        1.4,
      );
    }

    return tl;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new MaterialHeroFeature();
});
